var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import ResponsiveTable from "../ResponsiveTable/ResponsiveTable";
const mapToSortedRows = (rows, sortTable, sortInfo) => {
    if (!sortInfo || !sortInfo.direction) {
        return [rows, rows.map((_, i) => i)];
    }
    const sortedResults = rows
        .map((row, index) => ({ index, row }))
        .sort((a, b) => {
        // @ts-expect-error - TS2531 - Object is possibly 'null'. | TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type 'string | number | undefined[][]'.
        const aVal = sortTable[a.index][sortInfo.columnIndex];
        // @ts-expect-error - TS2531 - Object is possibly 'null'. | TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type 'string | number | undefined[][]'.
        const bVal = sortTable[b.index][sortInfo.columnIndex];
        const direction = sortInfo.direction === "asc" ? 1 : -1;
        // Handle null values.  Nulls should be last.
        if (aVal == null) {
            return Infinity;
        }
        if (bVal == null) {
            return -Infinity;
        }
        if (typeof aVal === "string") {
            if (typeof bVal !== "string" && console.warn) {
                console.warn(`Found values of different types in column of clientSideTable.  Val1: ${aVal}; Val2: ${bVal}`);
            }
            return direction * aVal.localeCompare(bVal.toString());
        }
        if (typeof aVal === "number") {
            if (typeof bVal !== "number") {
                if (console.warn) {
                    console.warn(`Found values of different types in column of clientSideTable.  Val1: ${aVal}; Val2: ${bVal}`);
                }
                return direction * aVal.toString().localeCompare(bVal);
            }
            return direction * (aVal - bVal);
        }
        if (console.warn) {
            console.warn(`ClientSideTable has invalid column value: ${aVal}`);
        }
        return 0;
    });
    return [
        sortedResults.map((result) => result.row),
        sortedResults.map((result) => result.index),
    ];
};
/**
 * A table which is sorted on the client side.
 *
 * In order to do this, you need to tell this component both how to display your data and how to sort it.
 * Sorting is done with the help of `sortTable`, which is a mapping of the Nodes being displayed in the table to either
 * strings or numbers.
 *
 * Let's look at an example: Suppose you want to display a table of data that looks like this:
 * ```js
 * {count: number, status : {label: string, id: number}}
 * ```
 * You specify how to display this data via the `rows` attribute. Rows expects a two-dimensional array of Nodes.
 * Suppose you have a `<Status/>` component which displays a status. Rows might be populated like this:
 *
 * ```jsx
 *  <ClientSideTable rows={data.map(row => [row.count,<Status status={row.status} />])} />
 * ```
 *
 * Now we need to sort these values.  To specify how to sort these values we populate the `sortTable` property.  This
 * is a two-dimensional array of either strings or numbers.  Think of this as each cell in the table being represented
 * as either a string or a number.  This component can then compare those values in order to sort the rows.  It's recommended
 * to make all cells in the same column the same type (either string or number).  Our example might look like this:
 *
 * ```jsx
 * <ClientSideTable ... sortTable={data.map(row => [row.count, row.status.label])} />
 * ```
 *
 * Finally, we need to specify what to call these columns and optionally how much space they should each take up.
 * This component uses the `<ResponsiveTable/>` component internally.  Check the documentation for that component
 * to learn more about what options should be passed in.
 *
 */
const ClientSideTable = (_a) => {
    var { columns, rows, sortTable, initialSort, addCheckboxes } = _a, otherProps = __rest(_a, ["columns", "rows", "sortTable", "initialSort", "addCheckboxes"]);
    const [sortInfo, setSortInfo] = React.useState(initialSort || null);
    const getSortHandler = (columnIndex) => (direction) => {
        setSortInfo({ columnIndex, direction });
    };
    const [sortedRows, sortMap] = React.useMemo(() => mapToSortedRows(rows, sortTable, sortInfo), [rows, sortInfo, sortTable]);
    return (_jsx(ResponsiveTable, Object.assign({}, otherProps, { columns: columns.map((column, index) => (Object.assign(Object.assign({}, column), { onSort: getSortHandler(index) }))), rows: sortedRows, addCheckboxes: addCheckboxes && {
            useCheckboxesMakeCheckbox: addCheckboxes.useCheckboxesMakeCheckbox,
            ids: addCheckboxes.rowIds.map((_, index) => addCheckboxes.rowIds[sortMap[index]]),
        }, initialSortColumn: initialSort === null || initialSort === void 0 ? void 0 : initialSort.columnIndex, initialSortDirection: initialSort === null || initialSort === void 0 ? void 0 : initialSort.direction })));
};
export default ClientSideTable;
